import { Fragment, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Transcript,
  TranscriptMessage,
  TranscriptNote,
  TranscriptRemark,
} from '@wundo/ui';
import type { RoleplayDebriefRemark } from '@wundo/shared/roleplay.ts';
import { transcriptTurns } from './transcriptTurns.ts';
import { fetchRoleplayTranscript } from '@/lib/roleplaySession.ts';
import { m } from '@/paraglide/messages.js';

const NO_REMARKS: readonly RoleplayDebriefRemark[] = [];

export interface PracticeTranscriptProps {
  sessionId: string;
  /** Who the user was talking to, named above everything they said. */
  personaName: string;
  /** Absent on feedback written before there were remarks. */
  remarks?: readonly RoleplayDebriefRemark[];
}

/**
 * What was said in one practice conversation, with Wundo's remarks under the
 * moments the feedback is about. Read as it was stored: nothing is tidied up,
 * which is what the note at the foot says.
 */
export function PracticeTranscript({
  sessionId,
  personaName,
  remarks = NO_REMARKS,
}: PracticeTranscriptProps) {
  const {
    data: messages,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['roleplayTranscript', sessionId],
    queryFn: () => fetchRoleplayTranscript(sessionId),
  });

  const turns = useMemo(
    () => transcriptTurns(messages ?? [], remarks),
    [messages, remarks],
  );

  if (isPending) {
    return <p className='text-base text-ink-muted'>{m.practice_transcript_loading()}</p>;
  }

  if (isError) {
    return (
      <p role='alert' className='text-base text-danger'>
        {m.practice_transcript_failed()}
      </p>
    );
  }

  return (
    <Transcript>
      {turns.map((turn) => {
        const side = turn.author === 'user' ? 'self' : 'other';

        return (
          <Fragment key={turn.id}>
            <TranscriptMessage
              side={side}
              speaker={side === 'self' ? m.practice_session_you() : personaName}
            >
              {turn.text}
            </TranscriptMessage>
            {turn.remarks.map((remark) => (
              <TranscriptRemark
                key={`${remark.message_id}:${remark.remark}`}
                side={side}
                author={m.chat_author_wundo()}
                betterPhrasing={
                  remark.better_phrasing?.trim()
                    ? m.practice_transcript_better_phrasing({
                        text: remark.better_phrasing,
                      })
                    : undefined
                }
              >
                {remark.remark}
              </TranscriptRemark>
            ))}
          </Fragment>
        );
      })}
      <TranscriptNote>{m.practice_transcript_note()}</TranscriptNote>
    </Transcript>
  );
}
